import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { modelClass, importCardXml } from '/imports/api/cards';
import { listSets } from '/imports/api/sets';
import { ModelListPage } from '/imports/ui/ModelListPage';
import { PageHeading } from '/imports/ui/PageHeading';
import { Button } from '/imports/ui/Button';
import { Icon } from '/imports/ui/Icon';
import { Select } from '/imports/ui/Select';
import { useMethod } from '/imports/hooks';
import { arrayBufferToBase64 } from '/imports/utils';

const CardsHeader = () => {
  const { pluralName, icon } = modelClass;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const onFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list?.length) return;
    setBusy(true);
    try {
      const files = await Promise.all(
        Array.from(list).map(async (f) => (
          // Artwork jpgs/pngs are binary -> base64; XML files stay utf8 text.
          /\.(jpe?g|png)$/i.test(f.name)
            ? { name: f.name, content: arrayBufferToBase64(await f.arrayBuffer()), encoding: 'base64' as const }
            : { name: f.name, content: await f.text() }
        )),
      );
      const res = await importCardXml.call({ files });
      toast.success(`Imported: ${res.cards} cards, ${res.cardNames} names, ${res.sets} sets, ${res.rarities} rarities, ${res.artwork} artwork`);
      if (res.skipped.length) toast.info(`Skipped: ${res.skipped.join(', ')}`);
    }
    catch (err: any) {
      toast.error(`${err}`);
    }
    finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <PageHeading
      left={<h1>{icon && <Icon icon={icon} /> || null}{pluralName}</h1>}
      right={
        <div>
          <Button className="btn primary" onClick={() => fileInputRef.current?.click()} disabled={busy}>
            {busy ? 'Importing…' : 'Import'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".xml,application/xml,text/xml,.jpg,.jpeg,.png,image/jpeg,image/png"
            style={{ display: 'none' }}
            onChange={onFiles}
          />
        </div>
      }
    />
  );
};

// Tailwind proof of concept: cell styling via utilities inside ModelListPage
// columns (no styled-components). Chip colors keyed by MTGO rarity.
const RARITY_CHIP: Record<string, string> = {
  MYTHIC: 'bg-orange/15 text-orange border-orange/40',
  MYTHIC_RARE: 'bg-orange/15 text-orange border-orange/40',
  RARE: 'bg-gold/15 text-gold border-gold/40',
  UNCOMMON: 'bg-grey-4/15 text-grey-2 border-grey-4/40',
  COMMON: 'bg-grey-7 text-grey-3 border-grey-5',
};
const RarityChip = ({ rarity }: { rarity?: string }) => {
  if (!rarity) return null;
  const chip = RARITY_CHIP[rarity] ?? 'bg-grey-8 text-grey-3 border-grey-6';
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-semibold tracking-wide ${chip}`}>
      {rarity}
    </span>
  );
};

const Tix = ({ value }: { value?: number | null }) => (
  <span className="font-mono tabular-nums text-ink">{typeof value === 'number' ? value.toFixed(2) : '·'}</span>
);

/**
 * The print-run label, readable. The XML calls every run "Cards …" ('Cards
 * Main Set', 'Cards Showcase Ugin Ten'), so the prefix is dropped; the main
 * set run renders as nothing at all because it is the overwhelming default
 * and a chip on every row would bury the variants this column exists to find.
 * A promo printing with no resolved label (COMPONENT_NAME_STRING.xml not part
 * of the last import) still shows a generic chip rather than hiding.
 */
const VariantChip = ({ variant, isPromoPrinting }: { variant?: string; isPromoPrinting?: boolean }) => {
  const label = (variant ?? '').replace(/^Cards\s+/, '').trim();
  if (!label && !isPromoPrinting) return null;
  if (/^Main Set$/i.test(label)) return null;
  return (
    <span className="inline-block rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
      {label || 'promo print'}
    </span>
  );
};

const PRINTING_OPTIONS = [
  { value: '', label: 'All printings' },
  { value: 'variants', label: 'Variants only' },
  { value: 'main', label: 'Main printings only' },
];

const FOIL_OPTIONS = [
  { value: '', label: 'Foil + regular' },
  { value: 'regular', label: 'Regular only' },
  { value: 'foil', label: 'Foil only' },
];

/** Newest set first, matching how an operator thinks about sets -- the set they
 *  care about is almost always the one that just released. `age` is MTGO's
 *  release-order index; sets without one sort to the end alphabetically. */
const SET_LIST_ARG = { limit: 1000, sort: { age: -1 as const } };

export const CardsPage = () => {
  const [setName, setSetName] = useState('');
  const [printing, setPrinting] = useState('');
  const [foil, setFoil] = useState('');
  // One fetch on mount: the set list is small (~419) and changes only when a
  // set is released, so it does not need to re-run as the filter changes.
  const [sets] = useMethod(listSets, SET_LIST_ARG, []);

  const setOptions = useMemo(() => {
    const docs = (sets?.documents ?? []) as { name?: string; fullName?: string; age?: number }[];
    const rows = docs
      .filter((d) => !!d.name)
      .sort((a, b) => (b.age ?? -1) - (a.age ?? -1) || String(a.name).localeCompare(String(b.name)))
      // The code is what the column shows and what operators search by, so lead
      // with it; the expansion name disambiguates the ones nobody memorises.
      .map((d) => ({ value: d.name as string, label: d.fullName ? `${d.name} — ${d.fullName}` : (d.name as string) }));
    return [{ value: '', label: 'All sets' }, ...rows];
  }, [sets]);

  return (
    <ModelListPage
      modelClass={modelClass as any}
      permission={(user) => !!user?.isSuperUser}
      header={<CardsHeader />}
      // ModelListPage re-syncs its internal query whenever this value changes
      // (it deps on JSON.stringify(query)), so returning a new object when the
      // set changes is what re-runs the list method server-side. Filtering
      // happens in Mongo, not on the fetched page -- important here, since the
      // catalog is paginated and a client-side filter would only ever search
      // whatever page you happened to be on.
      query={() => ({
        ...(setName ? { setName } : {}),
        // "Variants" = special-treatment printings (XML IS_PROMO). $ne true
        // rather than false for the main side, because every card imported
        // before the flag existed simply lacks it -- those are all mains.
        ...(printing === 'variants' ? { isPromoPrinting: true } : {}),
        ...(printing === 'main' ? { isPromoPrinting: { $ne: true } } : {}),
        ...(foil === 'foil' ? { isfoil: true } : {}),
        ...(foil === 'regular' ? { isfoil: false } : {}),
      } as any)}
      extraFilters={
        <>
          <Select
            className="filter"
            value={setName}
            options={setOptions}
            onChange={(v) => setSetName(v as string)}
          />
          <Select
            className="filter"
            value={printing}
            options={PRINTING_OPTIONS}
            onChange={(v) => setPrinting(v as string)}
          />
          <Select
            className="filter"
            value={foil}
            options={FOIL_OPTIONS}
            onChange={(v) => setFoil(v as string)}
          />
        </>
      }
      columns={[
        {
          field: '_id', label: 'ID',
          render: (card) => (
            <Link className="font-mono text-primary hover:text-primary-2 hover:underline" to={`${modelClass.pathPrefix}/${card._id}`}>
              {String(card._id)}
            </Link>
          ),
        },
        { field: 'name', label: 'Name' },
        { field: 'setName', label: 'Set', render: (card) => <span className="font-mono text-grey-2">{card.setName as string}</span> },
        // Collector number is what separates same-named printings at a glance
        // (Ugin TDM: 1 vs 382a vs 382b vs 399), so it sits beside the set.
        {
          field: 'collectorNumber', label: '#',
          render: (card) => <span className="font-mono tabular-nums text-grey-2">{(card.collectorNumber as string | undefined) ?? ''}</span>,
        },
        {
          field: 'variant', label: 'Variant',
          render: (card) => (
            <VariantChip
              variant={card.variant as string | undefined}
              isPromoPrinting={card.isPromoPrinting as boolean | undefined}
            />
          ),
        },
        { field: 'rarity', label: 'Rarity', render: (card) => <RarityChip rarity={card.rarity as string | undefined} /> },
        {
          field: 'isfoil', label: 'Foil',
          render: (card) => (card.isfoil
            ? <span className="inline-block rounded-full bg-tertiary/15 px-2 py-0.5 text-[11px] font-semibold text-tertiary">✦ foil</span>
            : null),
        },
        { field: 'manBuyPrice', label: 'Buy', className: 'mono', render: (card) => <Tix value={card.manBuyPrice as number | undefined} /> },
        { field: 'manSellPrice', label: 'Sell', className: 'mono', render: (card) => <Tix value={card.manSellPrice as number | undefined} /> },
      ]}
    />
  );
};
